import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { InputDatePicker } from '@/components/ui/InputDatePicker'
import { TimePicker } from '@/components/ui/timePicker'
import { useToast } from '@/components/ui/use-toast'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  EstateSettingNewRoundFormValues,
  useEstateSettingNewRoundSchema,
} from '@/estate/hooks/useEstateSettingNewRoundSchema'
import { EstateForSalePageFragment } from '@gql'
import { yupResolver } from '@hookform/resolvers/yup'
import { DEFAULT_DATE_FORMAT, parseDate } from '@almaris/shared/utils/dates'
import { addMonths, format } from 'date-fns'
import React, { useMemo } from 'react'
import { FieldErrors, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { trpc } from 'src/trpc'
import { EstateBuyerMemberMultiSelect } from '../EstateBuyerMember/EstateBuyerMemberMultiSelect'
import { useApolloClient } from '@apollo/client'

type EstateSettingNewRoundFormProps = {
  estateId: string
  currentRound: EstateForSalePageFragment['estateRounds'][number]
  readOnly?: boolean
  onSuccess?: () => void
}

export const EstateSettingNewRoundForm = ({
  estateId,
  currentRound,
  readOnly,
  onSuccess,
}: EstateSettingNewRoundFormProps) => {
  const { t } = useTranslation()
  const { toast } = useToast()

  const { schema } = useEstateSettingNewRoundSchema()

  const endDate = useMemo(() => {
    const date = parseDate(currentRound.endDate, DEFAULT_DATE_FORMAT)
    return date && addMonths(date, 1)
  }, [currentRound])

  const apolloClient = useApolloClient()

  const endTime = endDate && format(endDate, 'HH:mm')

  const newRoundForm = useForm<EstateSettingNewRoundFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      endDate,
      endTime,
      buyerMemberIds:
        currentRound.estateRoundBuyerMembers?.map(
          ({ buyerMemberId }) => buyerMemberId
        ) ?? [],
      isPublicRound: false,
      blindRound: false,
      enforceOfferGap: false,
      minOfferGap: undefined,
    },
  })

  const onSubmit = async ({
    endDate,
    endTime,
    buyerMemberIds,
    isPublicRound,
    blindRound,
    enforceOfferGap,
    minOfferGap,
  }: EstateSettingNewRoundFormValues) => {
    if (readOnly) return

    const [endHour, endMinute] = endTime.split(':').map(Number)
    endDate.setHours(endHour, endMinute, 0, 0)
    try {
      await trpc.estate.newRound.mutate({
        estateId,
        endDate,
        isPublic: isPublicRound,
        buyerMemberIds: isPublicRound ? [] : buyerMemberIds,
        options: {
          blindRound,
          enforceOfferGap,
          minOfferGap,
        },
      })
      toast({
        title: t(
          'EstateSettingOffersForm.newRound.toast.success.title'
        ) as string,
        description: t(
          'EstateSettingOffersForm.newRound.toast.success.description'
        ) as string,
        variant: 'success',
      })
      await apolloClient.refetchQueries({ include: ['getEstates'] })
      onSuccess?.()
    } catch (error) {
      console.error(error)
      toast({
        title: t(
          'EstateSettingOffersForm.newRound.toast.error.title'
        ) as string,
        description: t(
          'EstateSettingOffersForm.newRound.toast.error.description'
        ) as string,
        variant: 'destructive',
      })
    }
  }

  const onInvalid = (errors: FieldErrors<EstateSettingNewRoundFormValues>) => {
    console.log({ errors })
  }

  const handleBlindRoundChange = (value: boolean) => {
    if (value && newRoundForm.watch('enforceOfferGap')) {
      // Désactiver enforceOfferGap si on active blindRound
      newRoundForm.setValue('enforceOfferGap', false)
    }
    newRoundForm.setValue('blindRound', value)
  }

  const handleEnforceOfferGapChange = (value: boolean) => {
    if (value && newRoundForm.watch('blindRound')) {
      // Désactiver blindRound si on active enforceOfferGap
      newRoundForm.setValue('blindRound', false)
    }
    newRoundForm.setValue('enforceOfferGap', value)
  }

  const enforceOfferGap = newRoundForm.watch('enforceOfferGap')
  const isPublicRound = newRoundForm.watch('isPublicRound')

  return (
    <Form {...newRoundForm}>
      <form
        className="tw-space-y-6 tw-h-full"
        onSubmit={newRoundForm.handleSubmit(onSubmit, onInvalid)}
      >
        <div className="tw-flex tw-space-x-4">
          <FormField
            control={newRoundForm.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="tw-flex tw-flex-col tw-w-full">
                <FormLabel>
                  {t('EstateSettingOffersForm.form.endDate.date')}
                </FormLabel>
                <InputDatePicker
                  {...field}
                  placeholder={t(
                    'EstateSettingOffersForm.form.endDate.placeholder'
                  )}
                  fromDate={endDate}
                  disabled={readOnly}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={newRoundForm.control}
            name="endTime"
            render={({ field }) => (
              <FormItem className="tw-flex tw-flex-col tw-w-full">
                <FormLabel>
                  {t('EstateSettingOffersForm.form.endDate.hour')}
                </FormLabel>
                <TimePicker
                  {...field}
                  className="tw-w-full"
                  disabled={readOnly}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={newRoundForm.control}
          name="isPublicRound"
          render={({ field }) => (
            <FormItem className="tw-space-y-3">
              <FormLabel>
                {t('EstateSettingOffersForm.form.roundType.label')}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value ? 'public' : 'restricted'}
                  onValueChange={(value) => field.onChange(value === 'public')}
                  className="tw-flex tw-flex-col tw-space-y-1"
                  disabled={readOnly}
                >
                  <FormItem className="tw-flex tw-items-center tw-space-x-3">
                    <FormControl>
                      <RadioGroupItem value="public" />
                    </FormControl>
                    <FormLabel className="tw-font-normal">
                      {t('EstateSettingOffersForm.form.roundType.public')}
                    </FormLabel>
                  </FormItem>
                  <FormItem className="tw-flex tw-items-center tw-space-x-3">
                    <FormControl>
                      <RadioGroupItem value="restricted" />
                    </FormControl>
                    <FormLabel className="tw-font-normal">
                      {t('EstateSettingOffersForm.form.roundType.restricted')}
                    </FormLabel>
                  </FormItem>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={newRoundForm.control}
          name="blindRound"
          render={({ field }) => (
            <FormItem>
              <div className="tw-flex tw-items-center tw-space-x-2">
                <Switch
                  id="blind-round-new"
                  disabled={readOnly}
                  checked={field.value}
                  onCheckedChange={handleBlindRoundChange}
                />
                <Label
                  htmlFor="blind-round-new"
                  className="tw-text-sm tw-font-medium tw-leading-none peer-disabled:tw-cursor-not-allowed peer-disabled:tw-opacity-70"
                >
                  {t('EstateSettingOffersForm.form.blindRound')}
                </Label>
              </div>
              {field.value && (
                <p className="tw-text-sm tw-text-muted-foreground tw-ml-6">
                  {t('EstateSettingOffersForm.form.blindRoundDescription')}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={newRoundForm.control}
          name="enforceOfferGap"
          render={({ field }) => (
            <FormItem>
              <div className="tw-space-y-2">
                <div className="tw-flex tw-items-center tw-space-x-2">
                  <FormControl>
                    <Switch
                      id="enforce-offer-gap-new"
                      disabled={readOnly}
                      checked={field.value}
                      onCheckedChange={handleEnforceOfferGapChange}
                    />
                  </FormControl>
                  <Label
                    htmlFor="enforce-offer-gap-new"
                    className="tw-text-sm tw-font-medium tw-leading-none peer-disabled:tw-cursor-not-allowed peer-disabled:tw-opacity-70"
                  >
                    {t('EstateSettingOffersForm.form.enforceOfferGap')}
                  </Label>
                </div>
                {field.value && (
                  <p className="tw-text-sm tw-text-muted-foreground tw-ml-6">
                    {t(
                      'EstateSettingOffersForm.form.enforceOfferGapDescription'
                    )}
                  </p>
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {enforceOfferGap && (
          <FormField
            control={newRoundForm.control}
            name="minOfferGap"
            render={({ field }) => (
              <FormItem className="tw-ml-6">
                <div className="tw-flex tw-items-center tw-space-x-2">
                  <FormLabel
                    htmlFor="min-offer-gap-new"
                    className="tw-text-sm tw-font-medium"
                  >
                    {t('EstateSettingOffersForm.form.minOfferGap')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="min-offer-gap-new"
                      type="number"
                      min="0"
                      step="1000"
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      disabled={readOnly}
                      className="tw-w-32"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {!isPublicRound && (
          <FormField
            name="buyerMemberIds"
            render={({ field }) => (
              <EstateBuyerMemberMultiSelect
                estateId={estateId}
                defaultValue={field.value ?? []}
                onValueChange={field.onChange}
                disabled={readOnly}
                {...field}
              />
            )}
          />
        )}

        {!readOnly && (
          <Button variant="secondary" type="submit">
            {t('EstateSettingOffersForm.form.submit')}
          </Button>
        )}
      </form>
    </Form>
  )
}
